import React from 'react';
import {
  getOpenHoursString,
  validHighlyRatedFor,
  dateHasOpenHours,
  getLocationRatingAndRatingCount,
} from '../../../core/util/location';
import { getRelativeDayName } from '../../../core/util/date';
import moment from 'moment-timezone';
import { isEmptyArray } from '../../../core/util/array';
import { isEmptyObject, safeGet } from '../../../core/util/object';
import {
  FAQ_PATH,
  BLOG_PATH,
  URGENT_CARE_PATH,
  WALK_IN_CLINIC_PATH,
  PEDIATRIC_PATH,
  CITY_GUIDE_PATH,
} from '../../../constants';
import { BASE_URL } from '../../../config';
import { getCityDirectoryUrl } from '../../../core/util/seo';
import { createLocationUrlFromLocation } from '../../../core/url/location';
import { formatPhone, isEmptyString, sentenceToKebab } from '../../../core/util/string';
import { getMilesFromMeters } from '../../../core/util/geo';
import styled from 'styled-components';
import MarkDown from 'react-markdown';
import { CDP_INFO_DESCRIPTION, getCustomization } from '../util/customization';

const getNumDaysOpen = location => {
  const weekdays = Object.keys(location.hours);
  const daysOpen = weekdays.filter(weekday => !isEmptyArray(location.hours[weekday]));

  return daysOpen.length;
};

const getNumDaysOpenString = location => {
  const numDaysOpen = getNumDaysOpen(location);

  return numDaysOpen === 1 ? '1 day' : `${numDaysOpen} days`;
};

const getOpenHoursTodayString = location => {
  const appointmentDay = moment.tz(location.timeZone).valueOf();
  return getOpenHoursString({ location, date: appointmentDay });
};

const getRating = location => {
  const ratingAndRatingCount = getLocationRatingAndRatingCount(location);

  return ratingAndRatingCount.rating ? ratingAndRatingCount.rating.toFixed(1) : null;
};

const getRatingCount = location => {
  const ratingAndRatingCount = getLocationRatingAndRatingCount(location);

  return ratingAndRatingCount.ratingCount;
};

const getGlowingWords = location => {
  const web = safeGet(location)('scores.web');
  const validGlowingRatings = validHighlyRatedFor(web);
  const validGlowingWords = validGlowingRatings.map(rating => rating.label.toLowerCase());
  return validGlowingWords;
};

const getGlowingWordsString = location => {
  const glowingWords = getGlowingWords(location);

  const quotedWords = glowingWords.map(word => `"${word}"`);
  if (quotedWords.length === 1) {
    return quotedWords[0];
  }

  const lastWord = quotedWords[quotedWords.length - 1];
  const secondToLastWord = quotedWords[quotedWords.length - 2];
  const lastTwoWordsStr = `${secondToLastWord} and ${lastWord}`;

  if (quotedWords.length === 2) {
    return lastTwoWordsStr;
  }

  const otherWordsStr = quotedWords.slice(0, quotedWords.length - 2).join(', ');
  return `${otherWordsStr}, ${lastTwoWordsStr}`;
};

const getCityAnchorTag = location => {
  const { state, city } = location;
  const isLinkable = getCustomization(CDP_INFO_DESCRIPTION, { location }).shouldBeLinkable;
  if (!isLinkable) {
    return <span>{city}</span>;
  }

  return <a href={`${BASE_URL}${getCityDirectoryUrl(state, city.toLowerCase())}`}>{city}</a>;
};

const getWalkInClinicAnchorTag = () => {
  const isLinkable = getCustomization(CDP_INFO_DESCRIPTION, {}).shouldBeLinkable;
  if (!isLinkable) {
    return <span>walk-in urgent care</span>;
  }
  return <a href={`${BASE_URL}${WALK_IN_CLINIC_PATH}`}>walk-in urgent care</a>;
};

const getFaqsAnchorTag = () => {
  const isLinkable = getCustomization(CDP_INFO_DESCRIPTION, {}).shouldBeLinkable;
  if (!isLinkable) {
    return <span>FAQs</span>;
  }
  return <a href={`${BASE_URL}${FAQ_PATH}`}>FAQs</a>;
};

const getBlogAnchorTag = () => {
  const isLinkable = getCustomization(CDP_INFO_DESCRIPTION, {}).shouldBeLinkable;
  if (!isLinkable) {
    return <span>blog</span>;
  }
  return <a href={`${BASE_URL}${BLOG_PATH}`}>blog</a>;
};

const getSolvAnchorTag = () => {
  const isLinkable = getCustomization(CDP_INFO_DESCRIPTION, {}).shouldBeLinkable;
  if (!isLinkable) {
    return <span>Solv</span>;
  }
  return <a href={BASE_URL}>Solv</a>;
};

const getPhoneString = location => (location.phone ? ` at ${formatPhone(location.phone)}` : '');

const hasLocationHours = location => {
  for (let weekday in location.hours) {
    if (!isEmptyArray(location.hours[weekday])) {
      return true;
    }
  }

  return false;
};

const getNextOpenDayInfo = location => {
  const openDayInfo = {};

  for (let i = 0; i < 7; i++) {
    let date = moment(new Date()).add(i, 'days');

    if (dateHasOpenHours(location, date)) {
      const dayName = getRelativeDayName(date);
      openDayInfo.name =
        dayName === 'Today' || dayName === 'Tomorrow' ? dayName.toLowerCase() : dayName;
      openDayInfo.openHoursString = getOpenHoursString({ location, date });
      break;
    }
  }

  return openDayInfo;
};

const getClinicFullName = location =>
  location.displayNameSecondary
    ? `${location.displayNamePrimary}, ${location.displayNameSecondary}`
    : location.displayNamePrimary;

const getNearbyBrandLocation = nearbyLocations => safeGet(nearbyLocations)`brand.location`;

const getRetailPartner = location => !isEmptyObject(location) && location.insideOf;

const hasBrandName = nearbyLocations => !!safeGet(nearbyLocations)`brand.location.brandName`;

//make this a real function when we have phase 2 data
const getNumLocationsWithinBrand = nearbyBrands => nearbyBrands.brandCount;

const getLocationDistanceInMiles = location =>
  getMilesFromMeters(location.distanceFromCurrentLocation);

const getNearbyClinicDistanceStr = (nearbyClinic1, nearbyClinic2) => {
  const nearbyClinic1Distance = getLocationDistanceInMiles(nearbyClinic1);
  const nearbyClinic2Distance = getLocationDistanceInMiles(nearbyClinic2);
  return nearbyClinic1Distance && nearbyClinic2Distance
    ? `, which are ${nearbyClinic1Distance} and ${nearbyClinic2Distance} miles away,
        respectively`
    : '';
};

const getClinicAnchorTag = (location, copy = location.name) => {
  const clinicUrl = createLocationUrlFromLocation(location);
  const isLinkable = getCustomization(CDP_INFO_DESCRIPTION, { location }).shouldBeLinkable;
  if (!isLinkable) {
    return <span>{copy}</span>;
  }

  return <a href={clinicUrl}>{copy}</a>;
};

const getBrandName = location => !isEmptyObject(location) && location.brandName;

const getLastVerifiedDate = location => !isEmptyObject(location) && location.lastVerifiedOn;

const getLastVerifiedStr = location => {
  const lastVerifiedDate = getLastVerifiedDate(location);

  return lastVerifiedDate ? ` (last verified on ${lastVerifiedDate})` : '';
};

const getNearbySolvPartner = nearbyClinics =>
  !isEmptyArray(nearbyClinics) && nearbyClinics.find(location => location.isSolvPartner);

//make this a real function after phase 2
const getNearbyCities = location => [];

const hasNearbySolvPartner = nearbyClinics => !!getNearbySolvPartner(nearbyClinics);

const hasRating = location => !!getRating(location);

const getNumClinicsWithinCity = (nearbyClinics, city) =>
  nearbyClinics.filter(location => location.city === city).length;

const getNumClinicsWithin10Miles = nearbyClinics =>
  nearbyClinics.filter(location => getLocationDistanceInMiles(location) <= 10).length;

const hasAtLeast2NearbyLocations = nearbyLocations => {
  const nearbyClinics = safeGet(nearbyLocations)`nearby`;

  return !isEmptyArray(nearbyClinics) && nearbyClinics.length >= 2;
};

const hasCustomDescription = location => !isEmptyString(location.customDescription);

const NoTopMarginParagraph = styled.p`
  margin-block-start: 0;
`;

const NoBottomMarginParagraph = styled.p`
  margin-block-end: 0;
`;

const StyledMarkDown = styled(MarkDown)`
  p:first-child {
    margin-block-start: 0;
  }
`;

const getCityGuidePath = cityGuideName =>
  `${BASE_URL}${CITY_GUIDE_PATH}/${sentenceToKebab(cityGuideName)}-urgent-care`;

const generateCityGuideContent = (cityGuideName, userAgent) => {
  const isLinkable = getCustomization(CDP_INFO_DESCRIPTION, { userAgent }).shouldBeLinkable;
  if (!isLinkable) {
    return '';
  }

  return (
    <span>
      Learn more about <a href={getCityGuidePath(cityGuideName)}>urgent care in {cityGuideName}</a>.
    </span>
  );
};

const getCityGuideContent = (location, userAgent) => {
  switch (location.state) {
    case 'AZ':
      switch (location.city) {
        case 'Phoenix':
        case 'Scottsdale':
        case 'Tempe':
        case 'Paradise Valley':
        case 'Glendale':
        case 'Mesa':
        case 'Gilbert':
        case 'Chandler':
        case 'Peoria':
        case 'Tolleson':
        case 'Sun City':
        case 'Sun City West':
        case 'Carefree':
        case 'Litchfield Park':
        case 'Goodyear':
        case 'Surprise':
          return generateCityGuideContent('Phoenix', userAgent);
        default:
          return '';
      }
    case 'TX':
      switch (location.city) {
        case 'Arlington':
        case 'Azle':
        case 'Bedford':
        case 'Benbrook':
        case 'Burleson':
        case 'Carrollton':
        case 'Colleyville':
        case 'Crowley':
        case 'Dallas':
        case 'Denton':
        case 'Euless':
        case 'Fort Worth':
        case 'Frisco':
        case 'Garland':
        case 'Grapevine':
        case 'Haltom City':
        case 'Haslet':
        case 'Hurst':
        case 'Irving':
        case 'Keller':
        case 'Kennedale':
        case 'Mansfield':
        case 'McKinney':
        case 'Mesquite':
        case 'Plano':
        case 'North Richland Hills':
        case 'Roanoke':
        case 'Saginaw':
        case 'Southlake':
        case 'Watauga':
        case 'Westlake':
          return generateCityGuideContent('Dallas', userAgent);
        default:
          return '';
      }
    case 'CO':
      switch (location.city) {
        case 'Alma':
        case 'Arvada':
        case 'Aurora':
        case 'Black Hawk':
        case 'Brighton':
        case 'Broomfield':
        case 'Castle Pines North':
        case 'Castle Rock':
        case 'Centennial':
        case 'Central City':
        case 'Cherry Hills Village':
        case 'Coal Creek':
        case 'Commerce City':
        case 'Deer Trail':
        case 'Denver':
        case 'Downieville-Lawson-Dumont':
        case 'Empire':
        case 'Englewood':
        case 'Fairplay':
        case 'Federal Heights':
        case 'Georgetown':
        case 'Glendale':
        case 'Golden':
        case 'Greenwood Village':
        case 'Idaho Springs':
        case 'Kiowa':
        case 'Lakewood':
        case 'Littleton':
        case 'Lochbuie':
        case 'Lone Tree':
        case 'Northglenn':
        case 'Parker':
        case 'Silver Plume':
        case 'Simla':
        case 'Thornton':
        case 'Westminster':
        case 'Wheat Ridge':
          return generateCityGuideContent('Denver', userAgent);
        default:
          return '';
      }
    case 'CT':
      switch (location.city) {
        case 'Ansonia':
        case 'Bridgeport':
        case 'Danbury':
        case 'Derby':
        case 'Meriden':
        case 'Milford':
        case 'New Haven':
        case 'Norwalk':
        case 'Shelton':
        case 'Stamford':
        case 'Torrington':
        case 'Waterbury':
        case 'West Haven':
        case 'Winsted':
          return generateCityGuideContent('New York', userAgent);
        default:
          return '';
      }
    case 'NY':
      switch (location.city) {
        case 'Beacon':
        case 'Glen Cove':
        case 'Kingston':
        case 'Long Beach':
        case 'Middletown':
        case 'Mount Vernon':
        case 'New Rochelle':
        case 'New York City':
        case 'Newburgh':
        case 'Peekskill':
        case 'Port Jervis':
        case 'Poughkeepsie':
        case 'Rye':
        case 'White Plains':
        case 'Yonkers':
          return generateCityGuideContent('New York', userAgent);
        default:
          return '';
      }
    case 'NJ':
      switch (location.city) {
        case 'Newark':
          return generateCityGuideContent('New York', userAgent);
        default:
          return '';
      }
    case 'WA':
      switch (location.city) {
        case 'Arlington':
        case 'Auburn':
        case 'Bainbridge Island':
        case 'Bellevue':
        case 'Bonney Lake':
        case 'Bothell':
        case 'Bremerton':
        case 'Burien':
        case 'Covington':
        case 'Des Moines':
        case 'Duvall':
        case 'Edmonds':
        case 'Enumclaw':
        case 'Everett':
        case 'Federal Way':
        case 'Gig Harbor':
        case 'Gold Bar':
        case 'Issaquah':
        case 'Kenmore':
        case 'Kent':
        case 'Kirkland':
        case 'Lake Stevens':
        case 'Lakewood':
        case 'Lynnwood':
        case 'Maple Valley':
        case 'Marysville':
        case 'Mercer Island':
        case 'Mill Creek':
        case 'Monroe':
        case 'Mount Vernon':
        case 'Mountlake Terrace':
        case 'Mukilteo':
        case 'Newcastle':
        case 'Poulsbo':
        case 'Puyallup':
        case 'Redmond':
        case 'Renton':
        case 'Sammamish':
        case 'Seattle':
        case 'Shoreline':
        case 'Silverdale':
        case 'Tacoma':
        case 'Tukwila':
        case 'Woodinville':
          return generateCityGuideContent('Seattle', userAgent);
        default:
          return '';
      }
    default:
      return '';
  }
};

export {
  getCityAnchorTag,
  getGlowingWords,
  getGlowingWordsString,
  getNumDaysOpen,
  getOpenHoursTodayString,
  getRating,
  getRatingCount,
  getWalkInClinicAnchorTag,
  getFaqsAnchorTag,
  getBlogAnchorTag,
  getSolvAnchorTag,
  getPhoneString,
  hasLocationHours,
  getNextOpenDayInfo,
  getNumDaysOpenString,
  getClinicFullName,
  getNumLocationsWithinBrand,
  getClinicAnchorTag,
  hasBrandName,
  getBrandName,
  getLastVerifiedStr,
  getNearbySolvPartner,
  getNearbyCities,
  hasNearbySolvPartner,
  hasRating,
  hasCustomDescription,
  getLocationDistanceInMiles,
  getNumClinicsWithinCity,
  getNumClinicsWithin10Miles,
  getNearbyBrandLocation,
  getNearbyClinicDistanceStr,
  hasAtLeast2NearbyLocations,
  getRetailPartner,
  NoTopMarginParagraph,
  NoBottomMarginParagraph,
  StyledMarkDown,
  getCityGuideContent,
};
